import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogOut, Package, Edit, Save, X, Plus, Minus, ShoppingBag, Users, DollarSign, TrendingUp, Eye, CheckCircle, Clock, Truck, UserPlus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

// Second client to create users without disrupting admin session
const supabaseAdmin = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

export default function AdminPage() {
  const { user, profile, logout, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('orders');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [b2bUsers, setB2BUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [filter, setFilter] = useState('todos');
  const [orderFilter, setOrderFilter] = useState('all');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showCreateB2B, setShowCreateB2B] = useState(false);
  const [b2bForm, setB2bForm] = useState({ name: '', email: '', password: '', company: '', phone: '' });
  const [b2bCreating, setB2bCreating] = useState(false);

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (user && isAdmin) {
      if (activeTab === 'orders') fetchOrders();
      else if (activeTab === 'products') fetchProducts();
      else if (activeTab === 'b2b') fetchB2BUsers();
    }
  }, [user, isAdmin, activeTab, filter, orderFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    let query = supabase.from('orders').select('*, order_items(*)').order('created_at', { ascending: false });
    if (orderFilter !== 'all') query = query.eq('status', orderFilter);
    const { data, error } = await query;
    if (!error && data) setOrders(data);
    setLoading(false);
  };

  const fetchProducts = async () => {
    setLoading(true);
    let query = supabase.from('products').select('*');
    if (filter !== 'todos') query = query.eq('category', filter);
    const { data } = await query.order('category').order('name');
    if (data) setProducts(data);
    setLoading(false);
  };

  const fetchB2BUsers = async () => {
    setLoading(true);
    const { data } = await supabase.from('profiles').select('*').eq('role', 'b2b').order('created_at', { ascending: false });
    if (data) setB2BUsers(data);
    setLoading(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleEditProduct = (product) => {
    setEditingId(product.id);
    setEditData({ price: product.price, stock: product.stock, name: product.name });
  };

  const handleSaveProduct = async (productId) => {
    const { error } = await supabase.from('products').update({
      price: editData.price,
      stock: editData.stock,
      name: editData.name,
      updated_at: new Date().toISOString()
    }).eq('id', productId);

    if (!error) {
      toast.success('Producto actualizado');
      setEditingId(null);
      fetchProducts();
    } else {
      toast.error('Error al actualizar');
    }
  };

  const handleQuickStockChange = async (productId, change) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const newStock = Math.max(0, product.stock + change);
    const { error } = await supabase.from('products').update({ stock: newStock, updated_at: new Date().toISOString() }).eq('id', productId);
    if (!error) {
      toast.success(`Stock ${change > 0 ? 'aumentado' : 'reducido'}`);
      fetchProducts();
    }
  };

  const handleCreateB2B = async (e) => {
    e.preventDefault();
    setB2bCreating(true);
    try {
      if (b2bForm.password.length < 6) {
        toast.error('La contraseña debe tener al menos 6 caracteres');
        setB2bCreating(false);
        return;
      }
      const { data, error } = await supabaseAdmin.auth.signUp({
        email: b2bForm.email,
        password: b2bForm.password,
        options: { data: { name: b2bForm.name, role: 'b2b' } },
      });
      if (error) throw error;

      if (data.user) {
        await supabase.from('profiles').update({
          company: b2bForm.company || null,
          phone: b2bForm.phone || null,
        }).eq('id', data.user.id);
      }

      // Sign out the secondary client so it doesn't hold a session
      await supabaseAdmin.auth.signOut();

      toast.success(`Cliente Empresa "${b2bForm.name}" creado exitosamente`);
      setB2bForm({ name: '', email: '', password: '', company: '', phone: '' });
      setShowCreateB2B(false);
      fetchB2BUsers();
    } catch (err) {
      toast.error(err.message || 'Error al crear Cliente Empresa');
    } finally {
      setB2bCreating(false);
    }
  };

  const toggleProductActive = async (product) => {
    const { error } = await supabase.from('products').update({ is_active: !product.is_active }).eq('id', product.id);
    if (!error) {
      toast.success(product.is_active ? 'Producto desactivado' : 'Producto activado');
      fetchProducts();
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    const { error } = await supabase.from('orders').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', orderId);
    if (!error) {
      toast.success('Estado actualizado');
      fetchOrders();
    }
  };

  // Stats
  const todayOrders = orders.filter(o => new Date(o.created_at).toDateString() === new Date().toDateString());
  const totalRevenue = orders.filter(o => o.status !== 'cancelled').reduce((sum, o) => sum + o.total, 0);
  const pendingOrders = orders.filter(o => o.status === 'pending').length;

  const statusColors = {
    pending: 'bg-yellow-50 text-yellow-600',
    confirmed: 'bg-blue-50 text-blue-600',
    preparing: 'bg-purple-50 text-purple-600',
    ready: 'bg-green-50 text-green-600',
    delivered: 'bg-emerald-50 text-emerald-700',
    cancelled: 'bg-red-50 text-red-600',
  };

  const statusLabels = {
    pending: 'Pendiente',
    confirmed: 'Confirmado',
    preparing: 'Preparando',
    ready: 'Listo',
    delivered: 'Entregado',
    cancelled: 'Cancelado',
  };

  if (authLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2C5530] border-t-transparent"></div>
      </div>
    );
  }

  if (!user || !isAdmin) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="min-h-screen pt-20 md:pt-24 pb-16 bg-[#FAFAF7]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-cabinet font-bold text-2xl sm:text-3xl text-[#1A231C]">Panel de Administración</h1>
            <p className="text-[#808E83]">Bienvenido, {profile?.name || 'Admin'}</p>
          </div>
          <button onClick={handleLogout} className="inline-flex items-center gap-2 text-[#4A5D4E] hover:text-red-500 transition-colors">
            <LogOut size={18} /> Cerrar sesión
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><ShoppingBag className="text-blue-600" size={20} /></div>
              <div>
                <p className="text-xs text-[#808E83]">Pedidos Hoy</p>
                <p className="font-cabinet font-bold text-xl">{todayOrders.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center"><Clock className="text-yellow-600" size={20} /></div>
              <div>
                <p className="text-xs text-[#808E83]">Pendientes</p>
                <p className="font-cabinet font-bold text-xl">{pendingOrders}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center"><DollarSign className="text-green-600" size={20} /></div>
              <div>
                <p className="text-xs text-[#808E83]">Ventas Total</p>
                <p className="font-cabinet font-bold text-xl">${totalRevenue.toLocaleString('es-CL')}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center"><Users className="text-purple-600" size={20} /></div>
              <div>
                <p className="text-xs text-[#808E83]">Total Pedidos</p>
                <p className="font-cabinet font-bold text-xl">{orders.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {[
            { id: 'orders', label: '📦 Pedidos', icon: ShoppingBag },
            { id: 'products', label: '🏷️ Productos', icon: Package },
            { id: 'b2b', label: '👥 Clientes Empresa', icon: Users },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'bg-[#2C5530] text-white' : 'bg-white text-[#4A5D4E] hover:bg-[#F3F3EF]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ORDERS TAB */}
        {activeTab === 'orders' && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex flex-wrap gap-2 mb-6">
              {['all', 'pending', 'confirmed', 'preparing', 'ready', 'delivered', 'cancelled'].map(s => (
                <button
                  key={s}
                  onClick={() => setOrderFilter(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    orderFilter === s ? 'bg-[#2C5530] text-white' : 'bg-[#F3F3EF] text-[#4A5D4E]'
                  }`}
                >
                  {s === 'all' ? 'Todos' : statusLabels[s]}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="py-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2C5530] border-t-transparent mx-auto"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="py-12 text-center text-[#808E83]">No hay pedidos</div>
            ) : (
              <div className="space-y-4">
                {orders.map(order => (
                  <div key={order.id} className="border border-[#E8EAE6] rounded-xl overflow-hidden">
                    <div
                      className="flex flex-col sm:flex-row sm:items-center justify-between p-4 cursor-pointer hover:bg-[#FAFAF7]"
                      onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                    >
                      <div className="flex items-center gap-4">
                        <span className="font-cabinet font-bold text-sm text-[#808E83]">#{order.id}</span>
                        <div>
                          <strong className="block text-sm">{order.customer_name}</strong>
                          <span className="text-xs text-[#808E83]">{new Date(order.created_at).toLocaleString('es-CL')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-2 sm:mt-0">
                        <span className="text-xs px-2 py-1 rounded-full flex items-center gap-1">
                          {order.delivery_type === 'pickup' ? <MapPin size={12} /> : <Truck size={12} />}
                          {order.delivery_type === 'pickup' ? 'Retiro' : order.delivery_zone}
                        </span>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${statusColors[order.status]}`}>
                          {statusLabels[order.status]}
                        </span>
                        <strong className="text-sm text-[#2C5530]">${order.total.toLocaleString('es-CL')}</strong>
                      </div>
                    </div>

                    {expandedOrder === order.id && (
                      <div className="border-t border-[#E8EAE6] p-4 bg-[#FAFAF7]">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div className="text-sm space-y-1">
                            <p><strong>Teléfono:</strong> {order.customer_phone}</p>
                            {order.customer_email && <p><strong>Email:</strong> {order.customer_email}</p>}
                            {order.delivery_address && <p><strong>Dirección:</strong> {order.delivery_address}</p>}
                            {order.notes && <p><strong>Notas:</strong> {order.notes}</p>}
                          </div>
                          <div>
                            <p className="text-sm font-medium mb-2">Productos:</p>
                            <div className="space-y-1">
                              {order.order_items?.map(item => (
                                <div key={item.id} className="flex justify-between text-sm text-[#4A5D4E]">
                                  <span>{item.product_name} x{item.quantity}</span>
                                  <span>${item.subtotal.toLocaleString('es-CL')}</span>
                                </div>
                              ))}
                              {order.delivery_cost > 0 && (
                                <div className="flex justify-between text-sm text-[#4A5D4E] pt-1 border-t">
                                  <span>Envío</span>
                                  <span>${order.delivery_cost.toLocaleString('es-CL')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Status Actions */}
                        <div className="flex flex-wrap gap-2">
                          {order.status === 'pending' && (
                            <>
                              <button onClick={() => updateOrderStatus(order.id, 'confirmed')} className="px-3 py-1.5 bg-blue-500 text-white text-xs rounded-lg">Confirmar</button>
                              <button onClick={() => updateOrderStatus(order.id, 'cancelled')} className="px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg">Cancelar</button>
                            </>
                          )}
                          {order.status === 'confirmed' && (
                            <button onClick={() => updateOrderStatus(order.id, 'preparing')} className="px-3 py-1.5 bg-purple-500 text-white text-xs rounded-lg">Preparando</button>
                          )}
                          {order.status === 'preparing' && (
                            <button onClick={() => updateOrderStatus(order.id, 'ready')} className="px-3 py-1.5 bg-green-500 text-white text-xs rounded-lg">Listo</button>
                          )}
                          {order.status === 'ready' && (
                            <button onClick={() => updateOrderStatus(order.id, 'delivered')} className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg">Entregado</button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* PRODUCTS TAB */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex flex-wrap gap-2 mb-6">
              {['todos', 'espacio-fresco', 'pan', 'frutas', 'verduras', 'congelados', 'gourmet'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    filter === f ? 'bg-[#2C5530] text-white' : 'bg-[#F3F3EF] text-[#4A5D4E]'
                  }`}
                >
                  {f === 'todos' ? '🛒 Todos' : f}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="py-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2C5530] border-t-transparent mx-auto"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E8EAE6] text-left text-sm text-[#808E83]">
                      <th className="py-3 px-4">Producto</th>
                      <th className="py-3 px-4">Categoría</th>
                      <th className="py-3 px-4">Precio</th>
                      <th className="py-3 px-4 text-center">Stock</th>
                      <th className="py-3 px-4 text-center">Estado</th>
                      <th className="py-3 px-4 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => (
                      <tr key={product.id} className={`border-b border-[#E8EAE6] ${!product.is_active ? 'opacity-50' : ''}`}>
                        <td className="py-4 px-4">
                          {editingId === product.id ? (
                            <input
                              type="text"
                              value={editData.name}
                              onChange={e => setEditData({...editData, name: e.target.value})}
                              className="w-full px-2 py-1 border rounded-lg text-sm"
                            />
                          ) : (
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#F3F3EF] rounded-lg flex items-center justify-center text-lg">
                                {product.is_gourmet ? '🦀' : product.category === 'pan' ? '🥖' : '🥬'}
                              </div>
                              <div>
                                <strong className="block text-sm">{product.name}</strong>
                                <span className="text-xs text-[#808E83]">{product.unit}</span>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-4 text-sm">{product.category}</td>
                        <td className="py-4 px-4">
                          {editingId === product.id ? (
                            <input
                              type="number"
                              value={editData.price}
                              onChange={e => setEditData({...editData, price: parseInt(e.target.value) || 0})}
                              className="w-24 px-2 py-1 border rounded-lg text-sm text-center"
                            />
                          ) : (
                            <span className="text-sm font-medium">${product.price.toLocaleString('es-CL')}</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {editingId === product.id ? (
                            <input
                              type="number"
                              value={editData.stock}
                              onChange={e => setEditData({...editData, stock: parseInt(e.target.value) || 0})}
                              className="w-20 px-2 py-1 border rounded-lg text-sm text-center"
                              min="0"
                            />
                          ) : (
                            <div className="flex items-center justify-center gap-2">
                              <button onClick={() => handleQuickStockChange(product.id, -1)} className="p-1.5 bg-[#F3F3EF] rounded-lg hover:bg-red-50 hover:text-red-500">
                                <Minus size={14} />
                              </button>
                              <span className={`min-w-[40px] text-center font-semibold text-sm ${
                                product.stock === 0 ? 'text-red-500' : product.stock <= 5 ? 'text-amber-500' : 'text-[#2C5530]'
                              }`}>
                                {product.stock}
                              </span>
                              <button onClick={() => handleQuickStockChange(product.id, 1)} className="p-1.5 bg-[#F3F3EF] rounded-lg hover:bg-green-50 hover:text-green-500">
                                <Plus size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-4 text-center">
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            product.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
                          }`}>
                            {product.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-center gap-2">
                            {editingId === product.id ? (
                              <>
                                <button onClick={() => handleSaveProduct(product.id)} className="p-1.5 bg-[#2C5530] text-white rounded-lg"><Save size={16} /></button>
                                <button onClick={() => setEditingId(null)} className="p-1.5 bg-[#F3F3EF] rounded-lg"><X size={16} /></button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => handleEditProduct(product)} className="p-2 text-[#4A5D4E] hover:bg-[#F3F3EF] rounded-lg" title="Editar">
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => toggleProductActive(product)}
                                  className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                                    product.is_active ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'
                                  }`}
                                >
                                  {product.is_active ? 'Desactivar' : 'Activar'}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* B2B TAB */}
        {activeTab === 'b2b' && (
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-cabinet font-semibold text-xl flex items-center gap-3">
                <Users className="text-[#2C5530]" size={24} />
                Clientes Empresa
              </h2>
              <button
                onClick={() => setShowCreateB2B(!showCreateB2B)}
                className="flex items-center gap-2 bg-[#2C5530] hover:bg-[#3A6E40] text-white text-sm font-medium px-4 py-2 rounded-full transition-colors"
              >
                <UserPlus size={16} />
                Crear Cliente
              </button>
            </div>

            {/* Create B2B Form */}
            {showCreateB2B && (
              <div className="mb-6 p-6 bg-[#FAFAF7] rounded-xl border border-[#E8EAE6]">
                <h3 className="font-medium text-sm text-[#1A231C] mb-4">Nuevo Cliente Empresa</h3>
                <form onSubmit={handleCreateB2B} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Nombre *</label>
                    <input type="text" value={b2bForm.name} onChange={e => setB2bForm({...b2bForm, name: e.target.value})}
                      className="w-full px-3 py-2 border border-[#E8EAE6] rounded-lg text-sm focus:outline-none focus:border-[#2C5530]"
                      placeholder="Nombre completo" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Empresa</label>
                    <input type="text" value={b2bForm.company} onChange={e => setB2bForm({...b2bForm, company: e.target.value})}
                      className="w-full px-3 py-2 border border-[#E8EAE6] rounded-lg text-sm focus:outline-none focus:border-[#2C5530]"
                      placeholder="Nombre empresa (opcional)" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Email *</label>
                    <input type="email" value={b2bForm.email} onChange={e => setB2bForm({...b2bForm, email: e.target.value})}
                      className="w-full px-3 py-2 border border-[#E8EAE6] rounded-lg text-sm focus:outline-none focus:border-[#2C5530]"
                      placeholder="email@empresa.com" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Teléfono</label>
                    <input type="tel" value={b2bForm.phone} onChange={e => setB2bForm({...b2bForm, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-[#E8EAE6] rounded-lg text-sm focus:outline-none focus:border-[#2C5530]"
                      placeholder="+56 9 1234 5678 (opcional)" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#4A5D4E] mb-1">Contraseña *</label>
                    <input type="password" value={b2bForm.password} onChange={e => setB2bForm({...b2bForm, password: e.target.value})}
                      className="w-full px-3 py-2 border border-[#E8EAE6] rounded-lg text-sm focus:outline-none focus:border-[#2C5530]"
                      placeholder="Mínimo 6 caracteres" required minLength={6} />
                  </div>
                  <div className="flex items-end gap-2">
                    <button type="submit" disabled={b2bCreating}
                      className="bg-[#2C5530] hover:bg-[#3A6E40] disabled:bg-[#808E83] text-white text-sm font-medium px-6 py-2 rounded-lg transition-colors">
                      {b2bCreating ? 'Creando...' : 'Crear'}
                    </button>
                    <button type="button" onClick={() => { setShowCreateB2B(false); setB2bForm({ name: '', email: '', password: '', company: '', phone: '' }); }}
                      className="text-sm text-[#808E83] hover:text-red-500 px-4 py-2">
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            {loading ? (
              <div className="py-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#2C5530] border-t-transparent mx-auto"></div>
              </div>
            ) : b2bUsers.length === 0 ? (
              <div className="py-12 text-center text-[#808E83]">
                <p className="mb-2">No hay Clientes Empresa registrados</p>
                <p className="text-sm">Crea uno con el botón de arriba o comparte el enlace de registro</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#E8EAE6] text-left text-sm text-[#808E83]">
                      <th className="py-3 px-4">Nombre</th>
                      <th className="py-3 px-4">Email</th>
                      <th className="py-3 px-4">Empresa</th>
                      <th className="py-3 px-4">Teléfono</th>
                      <th className="py-3 px-4">Registro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {b2bUsers.map(u => (
                      <tr key={u.id} className="border-b border-[#E8EAE6]">
                        <td className="py-4 px-4 text-sm font-medium">{u.name}</td>
                        <td className="py-4 px-4 text-sm text-[#4A5D4E]">{u.email}</td>
                        <td className="py-4 px-4 text-sm">{u.company || '-'}</td>
                        <td className="py-4 px-4 text-sm">{u.phone || '-'}</td>
                        <td className="py-4 px-4 text-sm text-[#808E83]">{new Date(u.created_at).toLocaleDateString('es-CL')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function MapPin({ size = 16, ...props }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
    </svg>
  );
}
